import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';

import { AnalyticsService } from '@guardfinder/analytics';

import { Observable } from 'rxjs';

import { EnvironmentInterface } from '../../../../types';
import { ErrorDisplayService } from '../../../error';
import { UnitResult } from '../../../error/Domain/unit-result';
import { DavidResponseProcessor } from './david-response.processor';
import { ErrorContext, LaravelApiClientService } from './laravel-api-client.service';
import { LaravelErrorMapperService } from './laravel-error-mapper.service';

/**
 * AnalyticsLaravelApiClientService
 *
 * Extension of LaravelApiClientService that integrates with AnalyticsService to log API errors.
 *
 * This service enhances the base LaravelApiClientService by automatically logging API errors
 * to the analytics system. It uses the AnalyticsService's logApiError method to track
 * API failures, providing valuable data for monitoring and debugging.
 *
 * All other functionality remains identical to the parent LaravelApiClientService.
 */
@Injectable({
    providedIn: 'root'
})
export class AnalyticsLaravelApiClientService extends LaravelApiClientService {
    constructor(
        @Inject('environment') environment: EnvironmentInterface,
        http: HttpClient,
        errorMapper: LaravelErrorMapperService,
        errorService: ErrorDisplayService,
        davidResponseProcessor: DavidResponseProcessor,
        private readonly analytics: AnalyticsService
    ) {
        super(environment, http, errorMapper, errorService, davidResponseProcessor);
    }

    /**
     * Handles an error by logging it through analytics and then calling the parent class's catchError method.
     *
     * @param {ErrorContext} errorContext - The context of the error, including URL, status code, request data, and response error details.
     * @param {boolean} displayErrors - Flag that determines whether the errors should be displayed to the user.
     * @return {Observable<UnitResult<Response>>} An observable that represents the result of the error handling process.
     */
    protected override catchError<Response>(
        errorContext: ErrorContext,
        displayErrors: boolean
    ): Observable<UnitResult<Response>> {
        this.analytics
            .logApiError(
                errorContext.url,
                errorContext.errorResponse.status,
                errorContext.requestData,
                errorContext.errorResponse.error
            )
            .then();

        // Call the parent's catchError method to maintain the original functionality
        return super.catchError<Response>(errorContext, displayErrors);
    }
}
